import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { StatusBadge } from "../components/StatusBadge";
import { ChevronLeft, ChevronRight, Plus, List as ListIcon, Calendar as CalIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, clientName, formatApiError } from "../lib/format";
import { DIVISIONS, BOOKING_STATUS_LABEL, isManagement } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const SCHEDULE_TYPES = [
  { key: "fitting", label: "Fitting", tone: "bg-rose-50 text-rose-700 border-rose-200 border-l-rose-500" },
  { key: "tech_meeting", label: "Technical Meeting", tone: "bg-sky-50 text-sky-700 border-sky-200 border-l-sky-500" },
  { key: "gallery_consult", label: "Konsultasi Galeri", tone: "bg-amber-50 text-amber-800 border-amber-200 border-l-amber-500" },
  { key: "internal_meeting", label: "Meeting Internal Client", tone: "bg-emerald-50 text-emerald-700 border-emerald-200 border-l-emerald-500" },
];
const STATUS_LABEL = { planned: "Terjadwal", done: "Selesai", cancelled: "Batal", rescheduled: "Reschedule" };
const emptySchedule = { title: "", type: "fitting", scheduled_at: "", duration_minutes: 60, booking_id: "", client_id: "", division_id: "", location: "", notes: "" };

export default function CalendarPage() {
  const { user } = useAuth();
  const mgmt = isManagement(user);
  const [bookings, setBookings] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [div, setDiv] = useState("all");
  const [showBookings, setShowBookings] = useState(true);
  const [showSchedules, setShowSchedules] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptySchedule);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: bk }, { data: sc }] = await Promise.all([api.get("/bookings"), api.get("/schedules")]);
    setBookings(bk); setSchedules(sc);
  };
  useEffect(() => { load(); }, []);

  const filteredBookings = useMemo(() => bookings.filter((b) => {
    if (b.status === "Cancelled") return false;
    if (div !== "all" && !(b.services || []).some((s) => s.division_id === div)) return false;
    return true;
  }), [bookings, div]);
  const filteredSchedules = useMemo(() => schedules.filter((s) => {
    if (typeFilter !== "all" && s.type !== typeFilter) return false;
    if (div !== "all" && s.division_id && s.division_id !== div) return false;
    return true;
  }), [schedules, typeFilter, div]);

  // Merge event lists for a given date
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const byDay = {};
  if (showBookings) filteredBookings.forEach((b) => {
    if (!b.event_date) return;
    const dt = new Date(b.event_date);
    if (dt.getFullYear() === year && dt.getMonth() === month) {
      const day = dt.getDate();
      (byDay[day] = byDay[day] || []).push({ kind: "booking", data: b });
    }
  });
  if (showSchedules) filteredSchedules.forEach((s) => {
    if (!s.scheduled_at) return;
    const dt = new Date(s.scheduled_at);
    if (dt.getFullYear() === year && dt.getMonth() === month) {
      const day = dt.getDate();
      (byDay[day] = byDay[day] || []).push({ kind: "schedule", data: s });
    }
  });

  const isMuaFilter = div === "div-mua";
  const monthName = cursor.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const today = new Date();

  const openNew = () => { setEditingId(null); setForm(emptySchedule); setModalOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ title: s.title || "", type: s.type, scheduled_at: (s.scheduled_at || "").slice(0, 16),
              duration_minutes: s.duration_minutes || 60, booking_id: s.booking_id || "", client_id: s.client_id || "",
              division_id: s.division_id || "", location: s.location || "", notes: s.notes || "" });
    setModalOpen(true);
  };
  const submit = async () => {
    if (!form.title || !form.scheduled_at) { toast.error("Judul & waktu wajib"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      ["booking_id", "client_id", "division_id"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (editingId) await api.put(`/schedules/${editingId}`, payload);
      else await api.post("/schedules", payload);
      toast.success(editingId ? "Jadwal diperbarui" : "Jadwal dibuat");
      setModalOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeSchedule = async (s) => {
    if (!window.confirm(`Hapus jadwal "${s.title}"?`)) return;
    try { await api.delete(`/schedules/${s.id}`); toast.success("Jadwal dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Events this week (per booking + per schedule)
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const bookingsThisWeek = filteredBookings.filter((b) => { if (!b.event_date) return false; const d = new Date(b.event_date); return d >= monday && d <= sunday; }).length;
  const schedulesThisWeek = filteredSchedules.filter((s) => { if (!s.scheduled_at) return false; const d = new Date(s.scheduled_at); return d >= monday && d <= sunday; }).length;

  const allSorted = useMemo(() => {
    const items = [];
    if (showBookings) filteredBookings.forEach((b) => items.push({ kind: "booking", when: b.event_date, data: b }));
    if (showSchedules) filteredSchedules.forEach((s) => items.push({ kind: "schedule", when: s.scheduled_at, data: s }));
    return items.filter((x) => x.when).sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  }, [showBookings, showSchedules, filteredBookings, filteredSchedules]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Kalender & Jadwal Terpadu</h1>
          <p className="text-sm text-[#5C6066] mt-1">Acara booking + jadwal meeting (fitting, teknis, konsultasi galeri, internal) dalam satu tampilan.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="rounded-md border border-[#E5E0D8] bg-white px-3 py-1 text-xs" data-testid="events-this-week">
            <b>{bookingsThisWeek}</b> acara + <b>{schedulesThisWeek}</b> jadwal minggu ini
          </div>
          <div className="rounded-md border border-[#E5E0D8] bg-white p-1 flex">
            <button className={`px-3 py-1 text-xs rounded ${view==="calendar"?"bg-[#1E2022] text-white":"text-[#5C6066]"}`} onClick={() => setView("calendar")} data-testid="view-calendar"><CalIcon className="w-3 h-3 inline mr-1"/>Kalender</button>
            <button className={`px-3 py-1 text-xs rounded ${view==="row"?"bg-[#1E2022] text-white":"text-[#5C6066]"}`} onClick={() => setView("row")} data-testid="view-row"><ListIcon className="w-3 h-3 inline mr-1"/>Baris</button>
            <button className={`px-3 py-1 text-xs rounded ${view==="list"?"bg-[#1E2022] text-white":"text-[#5C6066]"}`} onClick={() => setView("list")} data-testid="view-list"><ListIcon className="w-3 h-3 inline mr-1"/>Tabel</button>
          </div>
          <Button className="bg-[#B7A175] hover:bg-[#A08A5D] text-white" onClick={openNew} data-testid="btn-new-schedule"><Plus className="w-4 h-4 mr-1"/>Jadwal Baru</Button>
        </div>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none p-3 flex flex-wrap items-center gap-2">
        <Select value={div} onValueChange={setDiv}>
          <SelectTrigger className="w-64" data-testid="calendar-div-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Divisi</SelectItem>
            {DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short} — {d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52" data-testid="calendar-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Jenis Jadwal</SelectItem>
            {SCHEDULE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-xs text-[#5C6066] px-2">
          <input type="checkbox" checked={showBookings} onChange={(e) => setShowBookings(e.target.checked)} data-testid="toggle-bookings"/>
          Tampilkan Acara Booking
        </label>
        <label className="flex items-center gap-1.5 text-xs text-[#5C6066] px-2">
          <input type="checkbox" checked={showSchedules} onChange={(e) => setShowSchedules(e.target.checked)} data-testid="toggle-schedules"/>
          Tampilkan Jadwal Meeting
        </label>
      </Card>

      {view === "calendar" && (
        <Card className="border border-[#E5E0D8] bg-white shadow-none">
          <div className="flex items-center justify-between p-4 border-b border-[#E5E0D8]">
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></Button>
            <div className="font-display font-semibold capitalize">{monthName}</div>
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4"/></Button>
          </div>
          <div className="grid grid-cols-7 text-xs uppercase tracking-wider text-[#8C9096] font-semibold border-b border-[#E5E0D8]">
            {["Sen","Sel","Rab","Kam","Jum","Sab","Min"].map((d) => <div key={d} className="px-3 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const isToday = d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
              const events = d ? (byDay[d] || []) : [];
              const bookingCount = events.filter((e) => e.kind === "booking").length;
              const weekday = d ? new Date(year, month, d).getDay() : 0;
              const isWeekend = weekday === 0 || weekday === 6;
              const overloaded = bookingCount >= 3 && (isMuaFilter || isWeekend);
              return (
                <div key={i} className={`min-h-[120px] border-r border-b border-[#F0EBE0] p-2 last:border-r-0 ${overloaded ? "bg-rose-50" : ""}`} data-testid={overloaded ? `cal-overload-${d}` : undefined}>
                  {d && <>
                    <div className="flex items-center gap-1 mb-1">
                      <div className={`text-xs font-semibold ${isToday ? "text-brand" : overloaded ? "text-rose-700" : "text-[#5C6066]"}`}>{d}</div>
                      {overloaded && <span className="text-[9px] font-bold text-rose-700 bg-rose-200 px-1 rounded">OVERLOAD</span>}
                      {overloaded && mgmt && (
                        <button className="ml-auto text-[9px] px-1 py-0.5 rounded bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold" onClick={async (e) => {
                          e.stopPropagation();
                          const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                          const newDate = window.prompt(`Geser semua jadwal ${isMuaFilter ? "MUA " : ""}pada ${dateStr} ke tanggal (YYYY-MM-DD):`, dateStr);
                          if (!newDate || newDate === dateStr) return;
                          try {
                            const payload = { date: dateStr, new_date: newDate };
                            if (isMuaFilter) payload.division_id = "div-mua";
                            const { data } = await api.post("/schedules/bulk-shift", payload);
                            toast.success(`${data.shifted} jadwal digeser`);
                            load();
                          } catch (err) { toast.error(err?.response?.data?.detail || "Gagal geser"); }
                        }} data-testid={`bulk-shift-${d}`} title="Geser semua jadwal">Geser</button>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {events.slice(0, 4).map((e, ei) => e.kind === "booking" ? (
                        <Link key={`b-${e.data.id}`} to={`/bookings/${e.data.id}`} className="block px-2 py-1 rounded text-[10px] bg-white hover:bg-[#F4ECE1] border-l-4 border-brand shadow-sm" data-testid={`cal-event-${e.data.booking_code}`}>
                          <div className="font-semibold text-[#1E2022] truncate">{clientName(e.data.client)}</div>
                          <div className="text-[9px] text-[#8C9096] truncate">{e.data.start_time || "10:00"} · {e.data.venue || e.data.location || "-"}</div>
                        </Link>
                      ) : (() => {
                        const t = SCHEDULE_TYPES.find((x) => x.key === e.data.type);
                        return (
                          <div key={`s-${e.data.id}`} onClick={() => openEdit(e.data)} className={`px-2 py-1 rounded text-[10px] cursor-pointer border-l-4 shadow-sm ${t?.tone || ""}`} title={`${t?.label}: ${e.data.title}`} data-testid={`cal-schedule-${e.data.id}`}>
                            <div className="font-semibold truncate">{e.data.title}</div>
                            <div className="text-[9px] opacity-70 truncate">{(e.data.scheduled_at||"").slice(11,16)} · {t?.label}</div>
                          </div>
                        );
                      })())}
                      {events.length > 4 && <div className="text-[9px] text-[#8C9096]">+{events.length - 4} lagi</div>}
                    </div>
                  </>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view === "row" && (
        <Card className="border border-[#E5E0D8] bg-white shadow-none">
          <div className="flex items-center justify-between p-4 border-b border-[#E5E0D8]">
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="row-prev"><ChevronLeft className="w-4 h-4"/></Button>
            <div className="font-display font-semibold capitalize">{monthName}</div>
            <Button variant="ghost" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="row-next"><ChevronRight className="w-4 h-4"/></Button>
          </div>
          <div className="divide-y divide-[#F0EBE0]">
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
              const events = byDay[d] || [];
              if (events.length === 0) return null;
              const dayObj = new Date(year, month, d);
              const dayName = dayObj.toLocaleDateString("id-ID", { weekday: "short" });
              const weekday = dayObj.getDay();
              const isWeekend = weekday === 0 || weekday === 6;
              const bookingCount = events.filter((e) => e.kind === "booking").length;
              const overloaded = bookingCount >= 3 && (isMuaFilter || isWeekend);
              return (
                <div key={d} className={`flex gap-3 p-3 ${overloaded ? "bg-rose-50" : ""}`} data-testid={`row-day-${d}`}>
                  <div className="w-16 shrink-0 text-center">
                    <div className={`text-[10px] uppercase font-semibold ${isWeekend ? "text-rose-700" : "text-[#8C9096]"}`}>{dayName}</div>
                    <div className={`text-2xl font-display font-bold ${overloaded ? "text-rose-700" : "text-[#1E2022]"}`}>{d}</div>
                    {overloaded && <span className="text-[9px] font-bold text-rose-700 bg-rose-200 px-1 rounded">OVERLOAD</span>}
                  </div>
                  <div className="flex-1 flex flex-wrap gap-2">
                    {events.map((e) => e.kind === "booking" ? (
                      <Link key={`b-${e.data.id}`} to={`/bookings/${e.data.id}`} className="px-3 py-2 rounded border-l-4 border-brand bg-white hover:bg-[#F4ECE1] shadow-sm min-w-[180px]" data-testid={`row-event-${e.data.booking_code}`}>
                        <div className="text-[10px] text-[#8C9096] font-mono">{e.data.booking_code} · {e.data.start_time || "10:00"}</div>
                        <div className="text-sm font-semibold">{clientName(e.data.client)}</div>
                        <div className="text-[10px] text-[#5C6066] truncate">{e.data.venue}</div>
                      </Link>
                    ) : (() => {
                      const t = SCHEDULE_TYPES.find((x) => x.key === e.data.type);
                      return (
                        <div key={`s-${e.data.id}`} onClick={() => openEdit(e.data)} className={`px-3 py-2 rounded border-l-4 shadow-sm min-w-[180px] cursor-pointer ${t?.tone || ""}`}>
                          <div className="text-[10px] font-mono opacity-70">{(e.data.scheduled_at||"").slice(11,16)} · {t?.label}</div>
                          <div className="text-sm font-semibold">{e.data.title}</div>
                          {e.data.client_name && <div className="text-[10px] opacity-70 truncate">{e.data.client_name}</div>}
                        </div>
                      );
                    })())}
                  </div>
                </div>
              );
            })}
            {Object.keys(byDay).length === 0 && <div className="p-10 text-center text-[#8C9096] text-sm">Tidak ada acara di {monthName}.</div>}
          </div>
        </Card>
      )}

      {view === "list" && (
        <Card className="border border-[#E5E0D8] bg-white shadow-none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[#5C6066]">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">Tanggal</th>
                  <th className="px-4 py-2 font-semibold">Jenis</th>
                  <th className="px-4 py-2 font-semibold">Judul / Client</th>
                  <th className="px-4 py-2 font-semibold">Lokasi</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EBE0]">
                {allSorted.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Tidak ada acara/jadwal.</td></tr>}
                {allSorted.map((it, idx) => it.kind === "booking" ? (
                  <tr key={`b-${it.data.id}`} className="hover:bg-[#FAF6EE]/40">
                    <td className="px-4 py-2">{formatDate(it.data.event_date)}</td>
                    <td className="px-4 py-2"><span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-[#FAF6EE] text-[#5C6066] border-l-2 border-brand">Acara Booking</span></td>
                    <td className="px-4 py-2"><Link to={`/bookings/${it.data.id}`} className="font-medium hover:underline">{clientName(it.data.client)}</Link><div className="text-[10px] text-[#8C9096]">{it.data.booking_code} · {it.data.event_name}</div></td>
                    <td className="px-4 py-2 text-xs">{it.data.venue || "-"}</td>
                    <td className="px-4 py-2"><StatusBadge status={it.data.status} label={BOOKING_STATUS_LABEL[it.data.status]} /></td>
                    <td className="px-4 py-2 text-right"><Link to={`/bookings/${it.data.id}`} className="text-xs text-brand hover:underline">Lihat →</Link></td>
                  </tr>
                ) : (() => {
                  const t = SCHEDULE_TYPES.find((x) => x.key === it.data.type);
                  return (
                    <tr key={`s-${it.data.id}`} className="hover:bg-[#FAF6EE]/40" data-testid={`schedule-row-${it.data.id}`}>
                      <td className="px-4 py-2">{formatDate(it.data.scheduled_at)} <span className="text-[10px] text-[#8C9096]">{(it.data.scheduled_at || "").slice(11, 16)}</span></td>
                      <td className="px-4 py-2"><span className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${t?.tone || ""}`}>{t?.label}</span></td>
                      <td className="px-4 py-2"><span className="font-medium">{it.data.title}</span>{it.data.client_name && <div className="text-[10px] text-[#8C9096]">{it.data.client_name}{it.data.booking_code ? ` · ${it.data.booking_code}` : ""}</div>}</td>
                      <td className="px-4 py-2 text-xs">{it.data.location || "-"}</td>
                      <td className="px-4 py-2 text-xs">{STATUS_LABEL[it.data.status] || it.data.status}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openEdit(it.data)} data-testid={`schedule-edit-${it.data.id}`}><Pencil className="w-3.5 h-3.5"/></Button>
                        {mgmt && <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-700" onClick={() => removeSchedule(it.data)} data-testid={`schedule-del-${it.data.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>}
                      </td>
                    </tr>
                  );
                })())}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="border border-[#E5E0D8] bg-white p-4 shadow-none">
        <div className="text-xs uppercase tracking-wide font-semibold text-[#8C9096] mb-3">Legenda</div>
        <div className="flex flex-wrap gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded border-l-2 border-brand bg-[#FAF6EE] text-[#5C6066]">Acara Booking</span>
          {SCHEDULE_TYPES.map((t) => <span key={t.key} className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${t.tone}`}>{t.label}</span>)}
          <span className="text-[10px] px-2 py-0.5 rounded bg-rose-200 text-rose-800 font-bold">OVERLOAD (≥3 acara di weekend / MUA)</span>
        </div>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="schedule-modal">
          <DialogHeader><DialogTitle>{editingId ? "Edit" : "Buat"} Jadwal</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2"><Label>Judul *</Label><Input className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="schedule-title" /></div>
            <div><Label>Jenis *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="schedule-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SCHEDULE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Waktu *</Label><Input type="datetime-local" className="mt-1" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="schedule-time" /></div>
            <div><Label>Durasi (menit)</Label><Input type="number" className="mt-1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></div>
            <div><Label>Lokasi</Label><Input className="mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Galeri DWG, Zoom, dll" /></div>
            <div className="col-span-2"><Label>Booking terkait (opsional)</Label>
              <Select value={form.booking_id || "none"} onValueChange={(v) => setForm({ ...form, booking_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Pilih booking..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tidak terkait booking</SelectItem>
                  {bookings.slice(0, 100).map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_code} · {b.event_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Catatan</Label><Textarea className="mt-1" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="schedule-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#B7A175] hover:bg-[#A08A5D] text-white" data-testid="schedule-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
